import React from 'react';
import type { RmaJob } from '../utils/rmaDb';
import { X, Printer } from 'lucide-react';

interface PrintTicketProps {
  job: RmaJob;
  onClose: () => void;
}

const PrintTicket: React.FC<PrintTicketProps> = ({ job, onClose }) => {
  const partsCost = job.parts.reduce((acc, p) => acc + (p.cost * p.qty), 0);
  const benchFee = job.parts.length > 0 ? 80 : 0;
  const total = job.actualCost || (partsCost + benchFee) || job.estimatedCost;
  const balanceDue = total - job.advancePayment;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto print:p-0 print:bg-white print:static print:overflow-visible">
      
      {/* Modal Container */}
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl border border-slate-100 flex flex-col max-h-[90vh] overflow-hidden print:shadow-none print:border-none print:max-h-full print:w-full">
        
        {/* Modal Actions Bar (hidden during printing) */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-100 flex justify-between items-center print:hidden">
          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-800 text-sm">Receipt & Work Order Preview</span>
            <span className="text-xs text-slate-400">({job.id})</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl shadow-sm transition-colors"
            >
              <Printer className="h-4 w-4" />
              Print Work Order
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 bg-white border border-slate-200 rounded-xl transition-colors"
            >
              <X className="h-4.5 w-4.5" />
            </button>
          </div>
        </div>

        {/* Printable Ticket Area */}
        <div id="printable-ticket" className="p-8 md:p-12 overflow-y-auto print:overflow-visible print:p-0">
          <div className="space-y-8 text-slate-850">
            
            {/* Header info */}
            <div className="flex justify-between items-start gap-4">
              <div>
                <h1 className="text-2xl font-black tracking-tight text-slate-900 uppercase">ProTech Repair Labs</h1>
                <p className="text-xs text-slate-500 mt-1">
                  128 Tech Avenue, Electronics District<br />
                  Phone: +1 (555) 900-RMA-FIX • Web: www.protechrepairs.com
                </p>
              </div>
              <div className="text-right flex flex-col items-end">
                <span className="text-xs font-bold uppercase text-slate-400 tracking-wider">Work Order / Ticket</span>
                <span className="text-xl font-extrabold text-slate-900 mt-0.5">{job.id}</span>
                <span className="text-xs text-slate-400 mt-1">Date: {new Date(job.createdAt).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Simulated Barcode */}
            <div className="border-t border-b border-slate-100 py-4 flex flex-col items-center justify-center gap-1">
              <div className="flex items-center justify-center tracking-normal select-none py-1">
                {/* Custom barcode layout using divs */}
                {[2, 1, 3, 1, 4, 2, 1, 3, 2, 1, 4, 1, 3, 2, 1, 2, 4, 1, 3, 1, 2, 3, 1, 2].map((w, idx) => (
                  <div 
                    key={idx} 
                    className="bg-black h-10" 
                    style={{ width: `${w}px`, marginRight: idx % 3 === 0 ? '2px' : '1px' }}
                  />
                ))}
              </div>
              <span className="text-[10px] font-mono tracking-[0.4em] text-slate-500 uppercase">{job.id}</span>
            </div>

            {/* Customer & Device Information */}
            <div className="grid grid-cols-2 gap-8 text-xs">
              
              <div className="space-y-2">
                <h3 className="font-extrabold text-slate-900 border-b border-slate-200 pb-1 uppercase tracking-wider">
                  Customer Details
                </h3>
                <div>
                  <p className="font-semibold text-slate-800 text-sm">{job.customerName}</p>
                  <p className="text-slate-500 mt-1">Phone: {job.customerPhone}</p>
                  <p className="text-slate-500 mt-0.5">Email: {job.customerEmail}</p>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="font-extrabold text-slate-900 border-b border-slate-200 pb-1 uppercase tracking-wider">
                  Device Details
                </h3>
                <div>
                  <p className="font-semibold text-slate-800 text-sm">
                    {job.deviceBrand} {job.deviceModel}
                  </p>
                  <p className="text-slate-500 mt-1">Category: {job.deviceCategory}</p>
                  <p className="text-slate-500 mt-0.5">Serial / IMEI: <span className="font-mono">{job.serialNumber}</span></p>
                </div>
              </div>
            </div>

            {/* Problems & Diagnostic Comments */}
            <div className="space-y-4 text-xs">
              <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                <h4 className="font-bold text-slate-800 uppercase tracking-wider mb-1">Reported Issue</h4>
                <p className="text-slate-700 leading-relaxed italic">{job.reportedProblem}</p>
              </div>

              {job.diagnostics && (
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100">
                  <h4 className="font-bold text-slate-800 uppercase tracking-wider mb-1">Diagnostic Log Comments</h4>
                  <p className="text-slate-700 leading-relaxed font-mono whitespace-pre-wrap">{job.diagnostics}</p>
                </div>
              )}
            </div>

            {/* Parts and Billing Breakdowns */}
            <div className="space-y-2">
              <h3 className="font-extrabold text-xs text-slate-900 border-b border-slate-200 pb-1 uppercase tracking-wider">
                Parts & Service Costs Billing
              </h3>
              
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-100 font-bold text-slate-500">
                    <th className="py-2">Item / Part</th>
                    <th className="py-2 text-right">Unit Price</th>
                    <th className="py-2 text-center w-16">Qty</th>
                    <th className="py-2 text-right w-24">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {/* Bench/Diagnostics Fee */}
                  <tr className="text-slate-700">
                    <td className="py-2">Labor Bench & Diagnostics Service Fee</td>
                    <td className="py-2 text-right">$80.00</td>
                    <td className="py-2 text-center">1</td>
                    <td className="py-2 text-right font-medium">$80.00</td>
                  </tr>

                  {/* Parts */}
                  {job.parts.map((p) => (
                    <tr key={p.id} className="text-slate-700">
                      <td className="py-2 font-medium">{p.name}</td>
                      <td className="py-2 text-right">${p.cost.toFixed(2)}</td>
                      <td className="py-2 text-center">{p.qty}</td>
                      <td className="py-2 text-right font-medium">${(p.cost * p.qty).toFixed(2)}</td>
                    </tr>
                  ))}

                  {/* Totals and balances */}
                  <tr className="border-t border-slate-200 font-bold">
                    <td colSpan={2} className="py-3"></td>
                    <td className="py-3 text-right">Total Est:</td>
                    <td className="py-3 text-right">${total.toFixed(2)}</td>
                  </tr>
                  <tr className="text-slate-500">
                    <td colSpan={2} className="py-1"></td>
                    <td className="py-1 text-right">Paid Deposit:</td>
                    <td className="py-1 text-right">-${job.advancePayment.toFixed(2)}</td>
                  </tr>
                  <tr className="border-t border-slate-900 text-sm font-black text-slate-900">
                    <td colSpan={2} className="py-2"></td>
                    <td className="py-2 text-right uppercase">Balance Due:</td>
                    <td className="py-2 text-right">${balanceDue.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Repair terms & conditions */}
            <div className="bg-slate-50 p-4 rounded-xl text-[9px] text-slate-500 leading-normal border border-slate-150">
              <h5 className="font-bold uppercase tracking-wider text-slate-700 mb-1">Repair Terms & Agreements</h5>
              <ol className="list-decimal list-inside space-y-1">
                <li>Warranty covers replaced parts and labor for 90 days from collection. Physical or liquid damages voids warranty.</li>
                <li>We do not take responsibility for secondary data loss. We suggest backing up files before device dropoff.</li>
                <li>Devices uncollected after 30 calendar days from notification are subject to storage fees or environmental disposal.</li>
              </ol>
            </div>

            {/* Signatures */}
            <div className="grid grid-cols-2 gap-12 pt-8 text-[11px]">
              <div className="flex flex-col items-center">
                <div className="w-full border-b border-slate-400 h-8"></div>
                <span className="text-slate-400 mt-2 font-medium">Customer Signature</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-full border-b border-slate-400 h-8 flex items-end justify-center">
                  <span className="font-mono text-slate-300 text-xs italic">Assigned Tech: {job.assignedTo.split(' ')[0]}</span>
                </div>
                <span className="text-slate-400 mt-2 font-medium">Technician Signature</span>
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  );
};

export default PrintTicket;
